import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import * as api from "../services/api";

type VerifyState = "idle" | "verifying" | "done" | "error";

interface SubscriptionData {
  subscription: {
    id: string;
    stripe_subscription_id: string;
    plan: string;
    status: string;
    current_period_start: string;
    current_period_end: string;
    created_at: string;
  } | null;
  plan: string;
  limits: {
    runs_per_day: number;
    premium_tools: boolean;
    priority_support: boolean;
  };
}

const planMeta: Record<string, { label: string; color: string; price: string }> = {
  free: { label: "Free", color: "bg-gray-100 text-gray-700", price: "$0/mo" },
  pro: { label: "Pro", color: "bg-indigo-100 text-indigo-700", price: "$19/mo" },
  enterprise: { label: "Enterprise", color: "bg-purple-100 text-purple-800", price: "$49/mo" },
};

const statusMeta: Record<string, { label: string; dot: string }> = {
  active: { label: "Active", dot: "bg-green-500" },
  canceled: { label: "Canceled", dot: "bg-red-500" },
  past_due: { label: "Past due", dot: "bg-amber-500" },
  trialing: { label: "Trialing", dot: "bg-blue-500" },
  incomplete: { label: "Incomplete", dot: "bg-gray-400" },
};

function fmt(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

export default function Billing() {
  const { user, loading: authLoading, setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const success = params.get("success") === "true";
  const canceled = params.get("canceled") === "true";
  const sessionId = params.get("session_id");

  const [data, setData] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [verifyState, setVerifyState] = useState<VerifyState>("idle");

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }

    const loadAndVerify = async () => {
      try {
        // If returning from Stripe checkout with a session_id, verify and apply the plan upgrade first
        if (success && sessionId && verifyState === "idle") {
          setVerifyState("verifying");
          try {
            await api.verifyCheckout(sessionId);
            setVerifyState("done");
            // Refresh the user context so navbar plan badge updates
            const me = await api.getMe();
            setUser(me.user);
          } catch {
            setVerifyState("error");
          }
        }
        const sub = await api.getSubscription();
        setData(sub);
      } catch (e: any) {
        setError(e?.message || "Failed to load billing info");
      } finally {
        setLoading(false);
      }
    };

    loadAndVerify();
  }, [user, authLoading]);

  const handlePortal = async () => {
    setPortalLoading(true);
    setError(null);
    try {
      const res = await api.createPortalSession();
      window.location.href = res.url;
    } catch (e: any) {
      setError("Billing portal is only available after subscribing. Please upgrade your plan first.");
      setPortalLoading(false);
    }
  };

  const handleUpgrade = async (plan: string) => {
    setUpgradeLoading(plan);
    setError(null);
    try {
      const res = await api.createCheckout(plan);
      window.location.href = res.url;
    } catch (e: any) {
      setError(e?.message || "Could not start checkout");
      setUpgradeLoading(null);
    }
  };

  const plan = data?.plan ?? user?.plan ?? "free";
  const meta = planMeta[plan] ?? planMeta.free;
  const sub = data?.subscription;
  const limits = data?.limits;
  const subStatus = sub?.status ?? (plan !== "free" ? "active" : null);
  const statusInfo = subStatus ? (statusMeta[subStatus] ?? statusMeta.active) : null;
  const isPaid = plan !== "free";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
          <p className="text-indigo-600 font-semibold uppercase tracking-widest text-sm mb-1">Account</p>
          <h1 className="text-3xl font-extrabold text-gray-900">Billing &amp; Subscription</h1>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-10 space-y-6">

        {/* Success / cancel banners */}
        {verifyState === "verifying" && (
          <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-2xl px-6 py-4 text-blue-800">
            <svg className="animate-spin h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
            <p className="font-medium text-sm">Activating your subscription — please wait…</p>
          </div>
        )}
        {success && verifyState !== "verifying" && (
          <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-2xl px-6 py-4 text-green-800">
            <span className="text-2xl">🎉</span>
            <div>
              <p className="font-bold text-base">Subscription activated!</p>
              <p className="text-sm mt-0.5">Your plan has been upgraded. Enjoy your new features — they are active right now.</p>
            </div>
          </div>
        )}
        {canceled && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-6 py-4 text-amber-800">
            <span className="text-2xl">ℹ️</span>
            <div>
              <p className="font-bold text-base">Checkout canceled</p>
              <p className="text-sm mt-0.5">No charge was made. You can try again whenever you are ready.</p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl px-6 py-4 text-red-700 text-sm font-medium">{error}</div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6 animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-1/3" />
                <div className="h-6 bg-gray-200 rounded w-1/2" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Current plan card */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-5">Current Plan</h2>
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${meta.color}`}>{meta.label}</span>
                    {statusInfo && (
                      <span className="flex items-center gap-1.5 text-sm text-gray-600">
                        <span className={`w-2 h-2 rounded-full ${statusInfo.dot}`} />
                        {statusInfo.label}
                      </span>
                    )}
                  </div>
                  <p className="text-3xl font-extrabold text-gray-900">{meta.price}</p>
                  {sub?.current_period_end && (
                    <p className="text-sm text-gray-500">
                      {sub.status === "canceled" ? "Access until" : "Renews"} {fmt(sub.current_period_end)}
                    </p>
                  )}
                  {sub?.current_period_start && (
                    <p className="text-xs text-gray-400">Subscribed {fmt(sub.current_period_start)}</p>
                  )}
                </div>

                <div className="flex flex-col gap-2 items-end">
                  {isPaid && sub?.stripe_subscription_id && !sub.stripe_subscription_id.startsWith("sub_test_") ? (
                    <button
                      onClick={handlePortal}
                      disabled={portalLoading}
                      className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold shadow hover:opacity-90 transition disabled:opacity-50"
                    >
                      {portalLoading ? "Loading…" : "Manage Billing →"}
                    </button>
                  ) : (
                    <Link
                      to="/pricing"
                      className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold shadow hover:opacity-90 transition"
                    >
                      Upgrade Plan →
                    </Link>
                  )}
                  {isPaid && (
                    <p className="text-xs text-gray-400">Manage, update payment, or cancel via Stripe</p>
                  )}
                </div>
              </div>

              {/* Limits */}
              {limits && (
                <div className="mt-6 pt-6 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Runs / day</p>
                    <p className="text-2xl font-extrabold text-gray-900">
                      {limits.runs_per_day === -1 ? "∞" : limits.runs_per_day}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Premium tools</p>
                    <p className="text-2xl font-extrabold text-gray-900">{limits.premium_tools ? "✓" : "—"}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Priority support</p>
                    <p className="text-2xl font-extrabold text-gray-900">{limits.priority_support ? "✓" : "—"}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Quick links / upgrade nudge */}
            <div className="space-y-4">
              {!isPaid && (
                <>
                  <div className="bg-indigo-600 rounded-2xl p-6 text-white shadow-md">
                    <p className="font-black text-lg mb-1">Go Pro for $19/mo</p>
                    <p className="text-indigo-200 text-sm mb-4">Unlimited runs, all tools, export, API access.</p>
                    <button
                      onClick={() => handleUpgrade("pro")}
                      disabled={upgradeLoading === "pro"}
                      className="w-full py-2.5 rounded-xl bg-white text-indigo-700 font-bold text-sm hover:bg-indigo-50 transition disabled:opacity-50"
                    >
                      {upgradeLoading === "pro" ? "Redirecting…" : "Upgrade to Pro →"}
                    </button>
                  </div>
                  <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
                    <p className="font-bold text-gray-900 mb-1">Enterprise</p>
                    <p className="text-sm text-gray-500 mb-3">Custom tools, SLA, team management, white-label.</p>
                    <button
                      onClick={() => handleUpgrade("enterprise")}
                      disabled={upgradeLoading === "enterprise"}
                      className="w-full py-2 rounded-xl border border-indigo-300 text-indigo-700 text-sm font-semibold hover:bg-indigo-50 transition disabled:opacity-50"
                    >
                      {upgradeLoading === "enterprise" ? "Redirecting…" : "Get Enterprise →"}
                    </button>
                  </div>
                </>
              )}

              <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm space-y-2.5 text-sm">
                <p className="font-bold text-gray-700 mb-1">Quick links</p>
                <Link to="/dashboard" className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition">
                  <span>📊</span> Dashboard
                </Link>
                <Link to="/tools" className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition">
                  <span>🛠</span> Browse tools
                </Link>
                <Link to="/pricing" className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition">
                  <span>💳</span> Compare plans
                </Link>
                <a href="mailto:[email protected]" className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition">
                  <span>✉️</span> Contact support
                </a>
              </div>
            </div>

          </div>
        )}

        {/* Plan comparison table */}
        {!loading && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-5">Plan Comparison</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="py-2 pr-6 text-gray-500 font-semibold w-1/2">Feature</th>
                    <th className="py-2 px-4 text-center font-bold text-gray-700">Free</th>
                    <th className={`py-2 px-4 text-center font-bold rounded-t-lg ${plan === "pro" ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}>Pro</th>
                    <th className={`py-2 px-4 text-center font-bold rounded-t-lg ${plan === "enterprise" ? "bg-purple-50 text-purple-800" : "text-gray-700"}`}>Enterprise</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {[
                    ["Tool runs / day", "3", "Unlimited", "Unlimited"],
                    ["All tools", "10 tools", "All 14 tools", "All 14 tools"],
                    ["Premium tools", "—", "✓", "✓"],
                    ["Export results", "—", "✓", "✓"],
                    ["API access", "—", "✓", "✓"],
                    ["Priority support", "—", "—", "✓"],
                    ["Custom tools", "—", "—", "✓"],
                    ["White-label", "—", "—", "✓"],
                    ["Price", "$0/mo", "$19/mo", "$49/mo"],
                  ].map(([feature, free, pro, ent]) => (
                    <tr key={feature} className="hover:bg-gray-50 transition">
                      <td className="py-3 pr-6 text-gray-700 font-medium">{feature}</td>
                      <td className={`py-3 px-4 text-center ${plan === "free" ? "font-bold text-gray-900" : "text-gray-400"}`}>{free}</td>
                      <td className={`py-3 px-4 text-center ${plan === "pro" ? "bg-indigo-50 font-bold text-indigo-700" : "text-gray-400"}`}>{pro}</td>
                      <td className={`py-3 px-4 text-center ${plan === "enterprise" ? "bg-purple-50 font-bold text-purple-800" : "text-gray-400"}`}>{ent}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!isPaid && (
              <div className="mt-5 flex gap-3 justify-end">
                <button
                  onClick={() => handleUpgrade("pro")}
                  disabled={!!upgradeLoading}
                  className="px-5 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold shadow hover:opacity-90 transition disabled:opacity-50"
                >
                  {upgradeLoading === "pro" ? "Redirecting…" : "Upgrade to Pro — $19/mo"}
                </button>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
